import { reactive, ref, Ref } from '@vue/composition-api';
import i18n, { i18n_t } from '@/locales/i18n';
import smh, { SMH } from '@/renderer/scripts/smh';
import tips from '@/renderer/scripts/utils/tips';
import { formatSize } from '@/renderer/scripts/utils/tools/index';
import { ECOption } from '@/renderer/components/charts/type';
interface SizeInfo {
  size: number;
  unit: string;
}
type Handler = {
  teamSpaceSizeInfo: SizeInfo;
  personalSpaceSizeInfo: SizeInfo;
  chartLoading: Ref<boolean>;
  chartOptions: ECOption;
  methods: {
    getSpaceStats: () => Promise<void>;
  };
};
export default function spaceHandler(): Handler {
  const teamSpaceSizeInfo = reactive({
    size: 0,
    unit: 'MB',
  });
  const personalSpaceSizeInfo = reactive({
    size: 0,
    unit: 'MB',
  });

  // 环形图配置
  const chartLoading = ref(false);
  const chartOptions: ECOption = reactive({
    color: ['#014fd1', '#ED7B2F', '#7742B3'],
    title: {
      show: true,
      top: '40%',
      left: 'center',
      text: i18n_t('使用总量（GB）'),
      textStyle: {
        color: '#ababab',
        fontSize: 14,
        fontWeight: 'normal',
      },
      subtext: '0',
      subtextStyle: {
        color: '#000',
        fontSize: 26,
        fontWeight: 'bold',
      },
    },
    series: [
      {
        type: 'pie',
        radius: ['75%', '90%'],
        avoidLabelOverlap: false,
        label: {
          show: false,
          position: 'center',
        },
        labelLine: {
          show: false,
        },
        data: [
          {
            value: 0,
            name: i18n_t('企业空间'),
            itemStyle: {
              borderWidth: 5,
              borderColor: '#fff',
            },
          },
          {
            value: 0,
            name: i18n_t('个人空间'),
            itemStyle: {
              borderWidth: 5,
              borderColor: '#fff',
            },
          },
        ],
      },
    ],
  });
  const methods = {
    // 容量占比
    async getSpaceStats() {
      // [企业空间，个人空间，其他占用]
      const spaceRate = [0, 0, 0];
      chartLoading.value = true;
      try {
        const res = await smh.userAPI.getSpaceStats({
          spaceTag: '',
        });
        if (res.data) {
          res.data.forEach(item => {
            if (item.spaceTag === 'team') {
              spaceRate[0] += +item.size;
            } else if (item.spaceTag === 'personal') {
              spaceRate[1] += +item.size;
            } else {
              spaceRate[2] += +item.size;
            }
          });
          Object.assign(teamSpaceSizeInfo, {
            size: formatSize(spaceRate[0]).size,
            unit: formatSize(spaceRate[0]).unit,
          });
          Object.assign(personalSpaceSizeInfo, {
            size: formatSize(spaceRate[1]).size,
            unit: formatSize(spaceRate[1]).unit,
          });
          chartLoading.value = false;
          methods.setOptions(spaceRate[0], spaceRate[1], spaceRate[2]);
        }
      } catch (e: MsgError) {
        chartLoading.value = false;
        tips.error(e);
      }
    },
    setOptions(teamSpace = 0, personalSpace = 0, other = 0) {
      const totalSize = teamSpace + personalSpace + other;
      Object.assign(chartOptions, {
        tooltip: {
          trigger: 'item',
          confine: true,
          formatter(params: any) {
            const { name, data, dataIndex, percent } = params;
            return `${name}: ${formatSize(data.value).size}${
              dataIndex === 0 ? teamSpaceSizeInfo.unit : personalSpaceSizeInfo.unit
            }  ${percent}%`;
          },
        },
        title: {
          show: true,
          top: '40%',
          left: 'center',
          text: i18n_t('使用总量（{slot1}）', {
            slot1: formatSize(totalSize).unit,
          }),
          textStyle: {
            color: '#ababab',
            fontSize: 14,
            fontWeight: 'normal',
          },
          subtext: `${formatSize(totalSize).size}`,
          subtextStyle: {
            color: '#000',
            fontSize: 26,
            fontWeight: 'blod',
          },
        },
        series: [
          {
            type: 'pie',
            radius: ['75%', '90%'],
            avoidLabelOverlap: false,
            label: {
              show: false,
              position: 'center',
            },
            labelLine: {
              show: false,
            },
            data: [
              {
                value: teamSpace,
                name: i18n_t('企业空间'),
                itemStyle: {
                  borderWidth: 2,
                  borderColor: '#fff',
                },
              },
              {
                value: personalSpace,
                name: i18n_t('个人空间'),
                itemStyle: {
                  borderWidth: 2,
                  borderColor: '#fff',
                },
              },
              other !== 0
                ? {
                    value: other,
                    name: i18n_t('其他'),
                    itemStyle: {
                      borderWidth: 2,
                      borderColor: '#fff',
                    },
                  }
                : undefined,
            ],
          },
        ],
      });
    },
  };
  return {
    teamSpaceSizeInfo,
    personalSpaceSizeInfo,
    chartLoading,
    chartOptions,
    methods,
  };
}
